import React from "react";
import clsx from "clsx";
import "../../../afs-walkthrough.css";

// Canadian corporate tax rates by province (small business / general)
const PROVINCIAL_RATES = {
	AB: { name: "Alberta",                  sb: 0.02,   gen: 0.08   },
	BC: { name: "British Columbia",         sb: 0.02,   gen: 0.12   },
	MB: { name: "Manitoba",                 sb: 0.00,   gen: 0.12   },
	NB: { name: "New Brunswick",            sb: 0.025,  gen: 0.14   },
	NL: { name: "Newfoundland & Labrador",  sb: 0.03,   gen: 0.15   },
	NS: { name: "Nova Scotia",              sb: 0.025,  gen: 0.14   },
	NT: { name: "Northwest Territories",    sb: 0.02,   gen: 0.115  },
	NU: { name: "Nunavut",                  sb: 0.03,   gen: 0.12   },
	ON: { name: "Ontario",                  sb: 0.032,  gen: 0.115  },
	PE: { name: "Prince Edward Island",     sb: 0.01,   gen: 0.16   },
	QC: { name: "Quebec",                   sb: 0.032,  gen: 0.115  },
	SK: { name: "Saskatchewan",             sb: 0.01,   gen: 0.12   },
	YT: { name: "Yukon",                    sb: 0.00,   gen: 0.12   },
};

const FEDERAL_SB_RATE = 0.09;
const FEDERAL_GEN_RATE = 0.15;
const SB_LIMIT = 500000;

function FinancialAnalytics({ balanceSheet, incomeStatement, province }) {
	let currentAssets = 0;
	let currentLiabilities = 0;
	let totalAssets = balanceSheet?.totalAssets?.current || 0;
	let totalLiabilities = balanceSheet?.totalLiabilities?.current || 0;
	let totalEquity = balanceSheet?.totalEquity?.current || 0;

	for (let section of (balanceSheet?.sections || [])) {
		if (section.category === "current-assets") currentAssets += section.totalCurrent;
		if (section.category === "current-liabilities") currentLiabilities += section.totalCurrent;
	}

	let revenue = 0;
	let cogs = 0;

	for (let section of (incomeStatement?.sections || [])) {
		if (section.category === "revenue") revenue += section.totalCurrent;
		if (section.category === "cogs") cogs += section.totalCurrent;
	}

	let grossProfit = revenue - cogs;
	let netIncome = incomeStatement?.netIncome?.current || 0;
	let workingCapital = currentAssets - currentLiabilities;

	let currentRatio = currentLiabilities !== 0 ? (currentAssets / currentLiabilities) : null;
	let debtToEquity = totalEquity !== 0 ? (totalLiabilities / totalEquity) : null;
	let grossMargin = revenue !== 0 ? ((grossProfit / revenue) * 100) : null;
	let netMargin = revenue !== 0 ? ((netIncome / revenue) * 100) : null;

	let roundTwo = function (value) {
		if (value === null) return null;
		return Math.round(value * 100) / 100;
	};

	let formatDollar = function (value) {
		if (value === 0 || value === null || value === undefined) return "–";
		let absolute = Math.abs(Math.round(value)).toLocaleString("en-CA");
		if (value < 0) return "($" + absolute + ")";
		return "$" + absolute;
	};

	let changes = [
		{ label: "Total Assets", current: totalAssets, prior: balanceSheet?.totalAssets?.prior || 0 },
		{ label: "Total Liabilities", current: totalLiabilities, prior: balanceSheet?.totalLiabilities?.prior || 0 },
		{ label: "Total Equity", current: totalEquity, prior: balanceSheet?.totalEquity?.prior || 0 },
		{ label: "Revenue", current: revenue, prior: 0 },
		{ label: "Net Income", current: netIncome, prior: incomeStatement?.netIncome?.prior || 0 },
	];

	for (let section of (incomeStatement?.sections || [])) {
		if (section.category === "revenue") changes[3].prior += section.totalPrior;
	}

	let ratioColor = function (value, good, bad) {
		if (value === null) return "";
		if (value >= good) return "afs-review__detail--pass";
		if (value <= bad) return "afs-review__detail--fail";
		return "";
	};

	return (
		<div>
			{/* Key Ratios */}
			<div className="afs-review__ai-card">
				<div className="afs-review__ai-card-title">Key Ratios</div>
				<div className="afs-review__ratio-row">
					<div className="afs-review__ratio-col">
						<div className={clsx("afs-review__ratio-value", ratioColor(currentRatio, 1.5, 1.0))}>{currentRatio !== null ? roundTwo(currentRatio) : "N/A"}</div>
						<div className="afs-review__detail">Current Ratio{currentRatio !== null && currentRatio >= 1.5 ? " — Healthy" : currentRatio !== null && currentRatio >= 1.0 ? " — Adequate" : currentRatio !== null ? " — Concerning" : ""}</div>
					</div>
					<div className="afs-review__ratio-col">
						<div className="afs-review__ratio-value">{debtToEquity !== null ? roundTwo(debtToEquity) : "N/A"}</div>
						<div className="afs-review__detail">Debt-to-Equity{debtToEquity !== null && debtToEquity < 1.0 ? " — Low" : debtToEquity !== null && debtToEquity <= 2.0 ? " — Moderate" : debtToEquity !== null ? " — High" : ""}</div>
					</div>
					<div className="afs-review__ratio-col">
						<div className="afs-review__ratio-value">{grossMargin !== null ? roundTwo(grossMargin) + "%" : "N/A"}</div>
						<div className="afs-review__detail">Gross Margin</div>
					</div>
					<div className="afs-review__ratio-col">
						<div className="afs-review__ratio-value">{netMargin !== null ? roundTwo(netMargin) + "%" : "N/A"}</div>
						<div className="afs-review__detail">Net Margin</div>
					</div>
				</div>
				<div className="afs-review__ratio-row">
					<div className="afs-review__ratio-col">
						<div className={clsx("afs-review__ratio-value", workingCapital >= 0 ? "afs-review__detail--pass" : "afs-review__detail--fail")}>{formatDollar(workingCapital)}</div>
						<div className="afs-review__detail">Working Capital</div>
					</div>
					<div className="afs-review__ratio-col">
						<div className={clsx("afs-review__ratio-value", netIncome >= 0 ? "afs-review__detail--pass" : "afs-review__detail--fail")}>{formatDollar(netIncome)}</div>
						<div className="afs-review__detail">Net Income</div>
					</div>
				</div>
			</div>

			{/* Year-over-Year */}
			<div className="afs-review__ai-card">
				<div className="afs-review__ai-card-title">Year-over-Year Changes</div>
				{changes.map(function (change, changeIndex) {
					let diff = change.current - change.prior;
					let pct = change.prior !== 0 ? roundTwo((diff / Math.abs(change.prior)) * 100) : null;
					let significance = diff > 0 ? "positive" : diff < 0 ? "negative" : "neutral";

					return (
						<div key={changeIndex} className="afs-review__yoy-row">
							<span className={clsx("afs-review__ai-badge", "afs-review__ai-badge--" + significance)}>{diff > 0 ? "↑" : diff < 0 ? "↓" : "—"}</span>
							<div className="afs-review__yoy-label">{change.label}</div>
							<div className="afs-review__yoy-values">
								<div>{formatDollar(change.current)}</div>
								<div className="afs-review__detail">Prior: {formatDollar(change.prior)}{pct !== null ? " (" + (diff > 0 ? "+" : "") + pct + "%)" : ""}</div>
							</div>
						</div>
					);
				})}
			</div>

			{/* Financial Position */}
			<div className="afs-review__ai-card">
				<div className="afs-review__ai-card-title">Financial Position</div>
				<div className="afs-review__position-row"><div className="afs-review__position-label">Total Assets</div><div className="afs-review__position-value">{formatDollar(totalAssets)}</div></div>
				<div className="afs-review__position-row"><div className="afs-review__position-label">Total Liabilities</div><div className="afs-review__position-value">{formatDollar(totalLiabilities)}</div></div>
				<div className="afs-review__position-row"><div className="afs-review__position-label">Total Equity</div><div className="afs-review__position-value">{formatDollar(totalEquity)}</div></div>
				<div className={clsx("afs-review__position-row", "afs-review__position-divider")}><div className="afs-review__position-label">Revenue</div><div className="afs-review__position-value">{formatDollar(revenue)}</div></div>
				<div className="afs-review__position-row"><div className="afs-review__position-label">Cost of Sales</div><div className="afs-review__position-value">{formatDollar(cogs)}</div></div>
				<div className="afs-review__position-row"><div className="afs-review__position-label">Gross Profit</div><div className="afs-review__position-value">{formatDollar(grossProfit)}</div></div>
				<div className="afs-review__position-row"><div className="afs-review__position-label"><strong>Net Income</strong></div><div className="afs-review__position-value afs-review__position-value--bold">{formatDollar(netIncome)}</div></div>
			</div>

			{/* Tax Computation */}
			{(function () {
				let taxExpense = 0;

				for (let section of (incomeStatement?.sections || [])) {
					if (section.category === "tax") taxExpense += section.totalCurrent;
				}

				let incomeBeforeTax = netIncome + taxExpense;
				if (incomeBeforeTax <= 0 && taxExpense === 0) return null;

				let provCode = (province || "ON").toUpperCase().replace(/[^A-Z]/g, "");
				if (provCode.length > 2) {
					let lookup = Object.entries(PROVINCIAL_RATES).find(function (entry) { return entry[1].name.toLowerCase().includes(provCode.toLowerCase()); });
					if (lookup) provCode = lookup[0];
					else provCode = "ON";
				}

				let provRates = PROVINCIAL_RATES[provCode] || PROVINCIAL_RATES.ON;
				let provName = provRates.name;

				let sbIncome = Math.min(Math.max(incomeBeforeTax, 0), SB_LIMIT);
				let genIncome = Math.max(incomeBeforeTax - SB_LIMIT, 0);

				let federalSB = sbIncome * FEDERAL_SB_RATE;
				let federalGen = genIncome * FEDERAL_GEN_RATE;
				let federalTotal = federalSB + federalGen;

				let provincialSB = sbIncome * provRates.sb;
				let provincialGen = genIncome * provRates.gen;
				let provincialTotal = provincialSB + provincialGen;

				let estimatedTax = federalTotal + provincialTotal;
				let effectiveRate = incomeBeforeTax !== 0 ? (estimatedTax / incomeBeforeTax * 100) : 0;

				let variance = taxExpense > 0 ? taxExpense - estimatedTax : null;

				let combinedSBRate = ((FEDERAL_SB_RATE + provRates.sb) * 100).toFixed(1);
				let combinedGenRate = ((FEDERAL_GEN_RATE + provRates.gen) * 100).toFixed(1);

				return (
					<div className="afs-review__ai-card">
						<div className="afs-review__ai-card-title">Tax Computation — {provName}</div>

						<div className="afs-review__position-row afs-review__position-divider">
							<div className="afs-review__position-label">Income Before Taxes</div>
							<div className="afs-review__position-value afs-review__position-value--bold">{formatDollar(incomeBeforeTax)}</div>
						</div>

						<div className="afs-review__position-row afs-review__position-row--mt8">
							<div className="afs-review__position-label">Federal — Small Business (9% on first $500K)</div>
							<div className="afs-review__position-value">{formatDollar(federalSB)}</div>
						</div>
						{genIncome > 0 && (
							<div className="afs-review__position-row">
								<div className="afs-review__position-label">Federal — General (15% on balance)</div>
								<div className="afs-review__position-value">{formatDollar(federalGen)}</div>
							</div>
						)}
						<div className="afs-review__position-row">
							<div className="afs-review__position-label">{provName} — Small Business ({(provRates.sb * 100).toFixed(1)}%)</div>
							<div className="afs-review__position-value">{formatDollar(provincialSB)}</div>
						</div>
						{genIncome > 0 && (
							<div className="afs-review__position-row">
								<div className="afs-review__position-label">{provName} — General ({(provRates.gen * 100).toFixed(1)}%)</div>
								<div className="afs-review__position-value">{formatDollar(provincialGen)}</div>
							</div>
						)}

						<div className="afs-review__position-row afs-review__position-divider afs-review__position-row--mt4">
							<div className="afs-review__position-label"><strong>Estimated Tax</strong> (combined rate: {combinedSBRate}%{genIncome > 0 ? " / " + combinedGenRate + "%" : ""})</div>
							<div className="afs-review__position-value afs-review__position-value--bold">{formatDollar(estimatedTax)}</div>
						</div>
						<div className="afs-review__position-row">
							<div className="afs-review__position-label">Effective Rate</div>
							<div className="afs-review__position-value">{effectiveRate.toFixed(1)}%</div>
						</div>

						{taxExpense > 0 && (
							<>
								<div className="afs-review__position-row afs-review__position-row--mt8">
									<div className="afs-review__position-label">Tax Provision on IS</div>
									<div className="afs-review__position-value">{formatDollar(taxExpense)}</div>
								</div>
								<div className="afs-review__position-row">
									<div className="afs-review__position-label">Variance (Provision − Estimated)</div>
									<div className={clsx("afs-review__position-value", Math.abs(variance) > 1000 && "afs-review__position-value--warn")}>
										{formatDollar(variance)}
									</div>
								</div>
							</>
						)}

						{taxExpense === 0 && incomeBeforeTax > 0 && (
							<div className="afs-review__tax-warning">
								No tax provision recorded. Consider adding a tax provision AJE for {formatDollar(estimatedTax)}.
							</div>
						)}
					</div>
				);
			})()}
		</div>
	);
}

export default FinancialAnalytics;
